import React, { useState, useEffect } from 'react';
import Thread from './Thread';
import { formatTimeAgo } from './utils';
import api from './api';

const MessageBoard = ({ board, communityName, onBack, user, onLoginRequired, onViewProfile }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');

  const isLoggedIn = !!user;

  useEffect(() => {
    fetchThreads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.id]);

  const fetchThreads = async () => {
    try {
      const response = await api.get(`/boards/${board.id}/threads`);
      setThreads(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching threads:', error);
      setLoading(false);
    }
  };

  const handleNewThreadClick = () => {
    if (!isLoggedIn) {
      if (onLoginRequired) onLoginRequired();
      return;
    }
    setShowCreateModal(true);
  };

  const handleCreateThread = async () => {
    if (!newThreadTitle.trim() || !user) return;

    try {
      const response = await api.post(`/boards/${board.id}/threads`, {
        title: newThreadTitle.trim(),
        content: newThreadContent.trim(),
      });

      setThreads([response.data, ...threads]);
      setNewThreadTitle('');
      setNewThreadContent('');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating thread:', error);
    }
  };

  if (selectedThread) {
    return (
      <Thread
        thread={selectedThread}
        boardName={board.name}
        onBack={() => {
          setSelectedThread(null);
          fetchThreads();
        }}
        user={user}
        onLoginRequired={onLoginRequired}
        onViewProfile={onViewProfile}
      />
    );
  }

  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#9ca3af'
      }}>
        Loading threads...
      </div>
    );
  }

  const getAuthorDisplay = (thread) => {
    if (thread.author_first_name) return thread.author_first_name;
    if (thread.author_name) return thread.author_name;
    return `User #${thread.author_id}`;
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#FF8A3D',
            cursor: 'pointer',
            fontSize: '1.25rem',
            padding: '0.5rem'
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <h3 style={{
            color: '#FFFFFF',
            fontSize: '1.5rem',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {board.icon} {board.name}
          </h3>
          <p style={{
            color: '#9ca3af',
            fontSize: '0.85rem',
            margin: '0.25rem 0 0 0'
          }}>
            {communityName}
          </p>
        </div>
        <button
          onClick={handleNewThreadClick}
          style={{
            padding: '0.5rem 1rem',
            background: '#FF8A3D',
            border: 'none',
            color: '#0B0B10',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: 600
          }}
        >
          + New Thread
        </button>
      </div>

      {/* Auth notice for non-logged-in users */}
      {!isLoggedIn && (
        <div style={{
          background: 'rgba(255, 138, 61, 0.1)',
          border: '1px solid rgba(255, 138, 61, 0.3)',
          borderRadius: '12px',
          padding: '1rem 1.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{ color: '#FF8A3D', margin: 0 }}>
            Sign in to start or join discussions
          </p>
          <button
            onClick={() => onLoginRequired && onLoginRequired()}
            style={{
              padding: '0.5rem 1rem',
              background: '#FF8A3D',
              border: 'none',
              color: '#0B0B10',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}
          >
            Sign In
          </button>
        </div>
      )}

      {/* Threads List */}
      {threads.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#9ca3af',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 138, 61, 0.2)'
        }}>
          <p style={{ marginBottom: '1rem' }}>No threads yet. Start the conversation!</p>
          <button
            onClick={handleNewThreadClick}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#FF8A3D',
              border: 'none',
              color: '#0B0B10',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            {isLoggedIn ? 'Create First Thread' : 'Sign in to Post'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {threads.map((thread) => (
            <div
              key={thread.id}
              onClick={() => setSelectedThread(thread)}
              style={{
                background: thread.is_pinned
                  ? 'rgba(255, 138, 61, 0.1)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: thread.is_pinned
                  ? '1px solid rgba(255, 138, 61, 0.4)'
                  : '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#FF8A3D';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = thread.is_pinned
                  ? 'rgba(255, 138, 61, 0.4)'
                  : 'rgba(255, 138, 61, 0.2)';
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.5rem'
              }}>
                <h4 style={{
                  color: '#FFFFFF',
                  fontSize: '1.1rem',
                  margin: 0,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {thread.is_pinned && <span style={{ color: '#FF8A3D' }}>{'\u{1F4CC}'}</span>}
                  {thread.title}
                </h4>
                <span style={{
                  color: '#9ca3af',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap'
                }}>
                  {formatTimeAgo(thread.updated_at)}
                </span>
              </div>

              {thread.content && (
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.9rem',
                  margin: '0 0 0.75rem 0',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {thread.content}
                </p>
              )}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                fontSize: '0.85rem',
                color: '#6b7280'
              }}>
                <span>
                  By {getAuthorDisplay(thread)}
                  {thread.author_verified && (
                    <span style={{ color: '#FC4C02', marginLeft: '0.35rem', fontSize: '0.75rem' }}>
                      Strava {'\u2713'}
                    </span>
                  )}
                </span>
                <span>{'\u2022'}</span>
                <span>
                  {thread.comment_count} {thread.comment_count === 1 ? 'reply' : 'replies'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Thread Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
        onClick={() => setShowCreateModal(false)}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              color: '#FFFFFF',
              marginBottom: '1.5rem',
              fontSize: '1.25rem'
            }}>
              New Thread in {board.name}
            </h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                color: '#9ca3af',
                fontSize: '0.9rem',
                marginBottom: '0.5rem'
              }}>
                Title *
              </label>
              <input
                type="text"
                value={newThreadTitle}
                onChange={(e) => setNewThreadTitle(e.target.value)}
                placeholder="What's this about?"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                color: '#9ca3af',
                fontSize: '0.9rem',
                marginBottom: '0.5rem'
              }}>
                Details (optional)
              </label>
              <textarea
                value={newThreadContent}
                onChange={(e) => setNewThreadContent(e.target.value)}
                placeholder="Add more context..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  color: '#9ca3af',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateThread}
                disabled={!newThreadTitle.trim()}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: newThreadTitle.trim() ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '8px',
                  cursor: newThreadTitle.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Post Thread
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBoard;
